import { computed, inject, signal, WritableSignal } from '@angular/core';
import { GeoJSON } from 'geojson';
// @ts-expect-error No type declaration type exist
import { stringify as wktStringify } from 'wkt';

import { FieldInterventionBloc } from '@connect-services/client/blocs/field-intervention.bloc';
import { FieldInterventionEntitiesListBloc } from '@connect-services/client/blocs/field-intervention-entities-list.bloc';
import { FieldInterventionLayerListBloc } from '@connect-services/client/blocs/field-intervention-layer-list.bloc';
import { MapBaseSource } from '@connect-services/client/components/map/map.component';
import { CheckboxState } from '@connect-services/client/lib/components/checkbox/checkbox.component';
import { LayerStyleTypeEnum } from '@connect-services/client/models/highlight-entity.model';
import { MapHighlightEntityService } from '@connect-services/client/services/map-highlight-entity.service';
import { MapGeoJSONFeatureWithLayer } from '@connect-services/client/services/map-popup.service';
import { PaginatorComponent } from '@connect-services/src/lib/components/paginator/paginator.component';
import {
    DataEntity,
    FieldInterventionEntity,
    Layer,
    LineString,
    MultiLineString,
    MultiPoint,
    MultiPolygon,
    Point,
    Polygon,
} from '@connect-services/src/sdk/api';
import { defaultPaginationParameters, PaginationParameters } from '@shared/client/models/pagination.model';

export abstract class AbstractFieldInterventionListService<
    EntityType extends FieldInterventionEntity | DataEntity,
    ActiveLayerType extends Layer,
> {
    public readonly activeLayers: WritableSignal<ActiveLayerType[]> = signal([]);
    public readonly currentSources: WritableSignal<MapBaseSource<unknown>[]> = signal([]);
    public readonly entities: WritableSignal<EntityType[]> = signal([]);
    public readonly entityType: WritableSignal<LayerStyleTypeEnum> = signal(LayerStyleTypeEnum.SYMBOL);
    public readonly fullSelectedLayers = signal<string[]>([]);
    public readonly headerCheckboxState = computed<CheckboxState>(() => {
        if (!this.activeLayers().length) {
            return CheckboxState.UNCHECKED;
        }

        return this.getHeaderCheckboxState(this.activeLayers().map((activeLayer) => activeLayer.id));
    });
    public readonly isActiveLayerFullySelected = computed(
        () =>
            this.fullSelectedLayers().includes(this.activeLayers()[0].id) &&
            !this.activeLayers().some((activeLayer) => this.unselectedEntities()[activeLayer.id].length),
    );
    public readonly isFieldInterventionSelectionActive = signal(false);
    public readonly isFilterOrSearchActive = computed(() => this.searchValue() || this.zoneDelimiter());
    public readonly isLoading = signal(false);
    public readonly isSubmitting = signal(false);
    public readonly isZoneDelimiterFilterActive = signal(false);
    public readonly paginationParameters = signal<PaginationParameters>({
        ...defaultPaginationParameters,
    });
    public paginator: PaginatorComponent | undefined;
    public readonly rowCheckboxState = computed<Record<string, CheckboxState>>(() =>
        this.getRowsCheckboxState(this.entities()),
    );
    public readonly searchValue = signal('');
    public readonly selectedEntities: WritableSignal<{ [key: string]: EntityType[] }> = signal({});
    public readonly selectedEntitiesNumber = computed(() =>
        Object.entries(this.selectedEntities()).reduce((accumulator, currentValue) => {
            const layerId = currentValue[0];
            const entitiesId = currentValue[1];

            if (this.fullSelectedLayers().includes(layerId)) {
                return accumulator + this.totalItemsByLayer()[layerId] - this.unselectedEntities()[layerId].length;
            }

            return accumulator + entitiesId.length;
        }, 0),
    );
    public readonly totalItemsByLayer: WritableSignal<{ [key: string]: number }> = signal({});
    public readonly unselectedEntities: WritableSignal<{ [key: string]: EntityType[] }> = signal({});
    public readonly zoneDelimiter = signal<string | undefined>(undefined);

    protected readonly fieldInterventionBloc = inject(FieldInterventionBloc);
    protected readonly fieldInterventionEntitiesListBloc = inject(FieldInterventionEntitiesListBloc);
    protected readonly fieldInterventionLayerListBloc = inject(FieldInterventionLayerListBloc);
    protected readonly mapHighlightEntityService = inject(MapHighlightEntityService);

    public async deleteZoneFilter(): Promise<void> {
        this.isZoneDelimiterFilterActive.set(false);
        this.zoneDelimiter.set(undefined);
        if (!this.activeLayers().length) {
            return;
        }

        await this.fetchEntities();
    }

    public getEntitiesToAdd(currentEntities: EntityType[], entity: EntityType): EntityType[] {
        const entityId = this.getEntityId(entity);

        const alreadyInList = currentEntities
            .map((currentDataEntity) => this.getEntityId(currentDataEntity))
            .includes(entityId);

        if (alreadyInList) {
            currentEntities = currentEntities.filter(
                (currentDataEntity: EntityType) => this.getEntityId(currentDataEntity) !== entityId,
            );

            return currentEntities;
        }
        currentEntities.push(entity);

        return currentEntities;
    }

    public getFieldInterventionAreaZoneDelimiter(): string | undefined {
        const fieldInterventionArea = this.fieldInterventionBloc.value()?.fieldInterventionArea;
        if (!fieldInterventionArea) {
            return undefined;
        }

        return wktStringify(fieldInterventionArea);
    }

    public async handleHeaderCheckBox(): Promise<void> {
        if (this.isFilterOrSearchActive()) {
            await this.handleSelectAllFilter();

            return;
        }

        await this.handleSelectAll();
    }

    public async handleSelectAllFilter(preSearchedEntities?: EntityType[]): Promise<void> {
        const searchedEntities: EntityType[] = preSearchedEntities ?? (await this.fetchAllEntities());
        if (!searchedEntities?.length) {
            return;
        }

        if (this.fullSelectedLayers().includes(this.activeLayers()[0].id)) {
            await this.handleFullSelectionFullSelectedLayerAndFilterActive(searchedEntities);

            return;
        }

        this.handleFullSelectionFilterActive(searchedEntities);
    }

    public handleSelectEntity(event: MouseEvent | undefined, entity: EntityType): void {
        event?.stopPropagation();

        const layerId = this.getLayerId(entity);

        const currentSource = this.currentSources().find((source) => source.sourceId === layerId);

        if (!currentSource) {
            return;
        }

        if (this.fullSelectedLayers().includes(layerId)) {
            this.unSelectAndHighlightEntity(entity, currentSource);

            return;
        }

        this.selectAndHighlightEntity(entity, currentSource);
    }

    public initSelectionObjects(layers: ActiveLayerType[]): void {
        for (const layer of layers) {
            this.selectedEntities.set({
                ...this.selectedEntities(),
                [layer.id]: this.selectedEntities()[layer.id] || [],
            });

            this.unselectedEntities.set({
                ...this.unselectedEntities(),
                [layer.id]: this.unselectedEntities()[layer.id] || [],
            });

            this.totalItemsByLayer.set({
                ...this.totalItemsByLayer(),
                [layer.id]: this.totalItemsByLayer()[layer.id] || 0,
            });
        }
    }

    public isEntitySelected(layerId: string, entityId: string): boolean {
        if (this.fullSelectedLayers().includes(layerId)) {
            return !this.hasEntity(this.unselectedEntities()[layerId], entityId);
        }

        return this.hasEntity(this.selectedEntities()[layerId], entityId);
    }
    public manageHighlight(): void {
        const firstActiveLayer = this.activeLayers()[0];
        if (!firstActiveLayer || !this.currentSources().length) {
            return;
        }

        //If one of the currentLayers is selected, all of them will be
        if (this.fullSelectedLayers().includes(firstActiveLayer.id)) {
            this.initFullSelectedHighLight();

            return;
        }

        this.initHighLight();
    }
    public async onPageChange(page: number): Promise<void> {
        this.paginationParameters.set({ ...this.paginationParameters(), page: page });

        await this.fetchEntities();
    }

    public resetPagination(): void {
        if (!this.paginator) {
            return;
        }

        this.paginationParameters.set(defaultPaginationParameters);
        this.paginator.resetPaginator();
    }

    public resetSelectionObjects(layers: ActiveLayerType[]): void {
        this.selectedEntities.set({});

        for (const layer of layers) {
            this.selectedEntities.set({
                ...this.selectedEntities(),
                [layer.id]: this.selectedEntities()[layer.id] || [],
            });

            this.unselectedEntities.set({
                ...this.unselectedEntities(),
                [layer.id]: this.unselectedEntities()[layer.id] || [],
            });
        }
    }

    public setEntityType(): void {
        if (!this.entities()[0]?.geom?.type) {
            return;
        }

        switch (this.entities()[0].geom?.type) {
            case MultiPoint.TypeEnum.MULTI_POINT:
            case Point.TypeEnum.POINT:
                this.entityType.set(LayerStyleTypeEnum.SYMBOL);
                break;
            case MultiLineString.TypeEnum.MULTI_LINE_STRING:
            case LineString.TypeEnum.LINE_STRING:
                this.entityType.set(LayerStyleTypeEnum.LINE);
                break;
            case MultiPolygon.TypeEnum.MULTI_POLYGON:
            case Polygon.TypeEnum.POLYGON:
                this.entityType.set(LayerStyleTypeEnum.FILL);
                break;
        }
    }

    public setSignalValue<T>(signal: WritableSignal<{ [key: string]: T }>, index: string, value: T): void {
        signal.set({ ...signal(), [index]: value });
    }

    public toggleEntities(entities: EntityType[]): void {
        if (!entities.length) {
            return;
        }

        const layerIds = new Set(entities.map((entity: EntityType) => this.getLayerId(entity)));

        entities.forEach((entity: EntityType) => this.toggleEntityInLayerState(entity));
        layerIds.forEach((layerId: string) => this.refreshHighlightForLayer(layerId));
    }

    public toggleEntitiesFromMapFeatures(features: MapGeoJSONFeatureWithLayer[]): void {
        const entities: EntityType[] = features
            .filter((feature: MapGeoJSONFeatureWithLayer) =>
                this.activeLayers().some((layer: ActiveLayerType) => layer.id === feature.source),
            )
            .map(this.buildEntityFromMapFeature.bind(this))
            .filter((entity: EntityType | undefined): entity is EntityType => !!entity);

        this.toggleEntities(entities);
    }

    private getFinalHeaderCheckBoxState(headerCheckboxStates: CheckboxState[]): CheckboxState {
        const allSame = !headerCheckboxStates.some(
            (headerCheckboxState) => headerCheckboxState !== headerCheckboxStates[0],
        );
        const hasIndeterminate = headerCheckboxStates.includes(CheckboxState.INDETERMINATE);
        const hasChecked = headerCheckboxStates.includes(CheckboxState.CHECKED);

        if (allSame) {
            return headerCheckboxStates[0];
        }

        if (hasIndeterminate) {
            return CheckboxState.INDETERMINATE;
        }

        if (hasChecked) {
            return CheckboxState.CHECKED;
        }

        return CheckboxState.UNCHECKED;
    }

    private getHeaderCheckboxState(activeLayerIds: string[]): CheckboxState {
        const headerCheckboxStates: CheckboxState[] = [];

        for (const activeLayerId of activeLayerIds) {
            if (this.unselectedEntities()[activeLayerId]?.length) {
                headerCheckboxStates.push(CheckboxState.INDETERMINATE);
                continue;
            }

            if (this.fullSelectedLayers().includes(activeLayerId)) {
                headerCheckboxStates.push(CheckboxState.CHECKED);
                continue;
            }

            if (!this.selectedEntities()[activeLayerId]?.length) {
                headerCheckboxStates.push(CheckboxState.UNCHECKED);
                continue;
            }

            headerCheckboxStates.push(CheckboxState.INDETERMINATE);
        }

        return this.getFinalHeaderCheckBoxState(headerCheckboxStates);
    }

    private getRowsCheckboxState(entities: EntityType[]): { [key: string]: CheckboxState } {
        return entities.reduce((accumulator: { [key: string]: CheckboxState }, currentValue: EntityType) => {
            let checkboxState = CheckboxState.UNCHECKED;

            const currentValueId = this.getEntityId(currentValue);
            const layerId = this.getLayerId(currentValue);

            const selectedEntitiesForLayer = this.selectedEntities()[layerId] ?? [];
            const unselectedEntitiesForLayer = this.unselectedEntities()[layerId] ?? [];

            const isLayerSelected = this.fullSelectedLayers().includes(layerId);

            const isEntitySelected = selectedEntitiesForLayer
                .map((entity) => this.getEntityId(entity))
                .includes(currentValueId);

            const isEntityUnselected = unselectedEntitiesForLayer
                .map((entity) => this.getEntityId(entity))
                .includes(currentValueId);

            if ((isLayerSelected && !isEntityUnselected) || isEntitySelected) {
                checkboxState = CheckboxState.CHECKED;
            }

            return { ...accumulator, [currentValueId]: checkboxState };
        }, {});
    }

    private handleEntitySelectionWithFilter(
        currentSelectedDataEntities: EntityType[],
        searchedDataEntities: EntityType[],
        selectAll: boolean,
        layerId: string,
    ): EntityType[] {
        let layerSelectedData: EntityType[] = currentSelectedDataEntities.filter(
            (selectedDataEntity) =>
                !searchedDataEntities
                    .map((searchedDataEntity) => this.getEntityId(searchedDataEntity))
                    .includes(this.getEntityId(selectedDataEntity)),
        );

        if (selectAll) {
            const searchEntitiesByLayer = searchedDataEntities.filter(
                (searchedDataEntity) => this.getLayerId(searchedDataEntity) === layerId,
            );

            layerSelectedData = Array.from(
                new Map(
                    [...currentSelectedDataEntities, ...searchEntitiesByLayer].map((entity) => [
                        this.getEntityId(entity),
                        entity,
                    ]),
                ).values(),
            );
        }

        return layerSelectedData;
    }

    // This method is used when you want to select or unselect all entities, and you have a filter active

    private handleFullSelectionFilterActive(searchedEntities: EntityType[]): void {
        const searchedEntityIds = new Set(
            searchedEntities
                .filter((entity: EntityType): entity is EntityType => !!entity)
                .map((entity: EntityType) => this.getEntityId(entity)),
        );

        const selectedEntityIds = new Set(
            this.activeLayers()
                .flatMap((activeLayer: ActiveLayerType) => this.selectedEntities()[activeLayer.id] ?? [])
                .filter((selectedEntity: EntityType | undefined): selectedEntity is EntityType => !!selectedEntity)
                .map((selectedEntity: EntityType) => this.getEntityId(selectedEntity)),
        );

        const areAllSearchedEntitiesSelected = [...searchedEntityIds].every((searchedEntityId: string) =>
            selectedEntityIds.has(searchedEntityId),
        );

        for (const layer of this.activeLayers()) {
            const currentSelectedDataEntities: EntityType[] = this.selectedEntities()[layer.id] ?? [];

            const layerSelectedData: EntityType[] = this.handleEntitySelectionWithFilter(
                currentSelectedDataEntities,
                searchedEntities,
                !areAllSearchedEntitiesSelected,
                layer.id,
            );

            this.setSignalValue<EntityType[]>(this.selectedEntities, layer.id, layerSelectedData);

            const currentSource = this.currentSources().find((source) => source.sourceId === layer.id);

            if (!currentSource || !layerSelectedData.length) {
                this.mapHighlightEntityService.resetHighlight();

                continue;
            }

            this.mapHighlightEntityService.manageHighlightFeature(
                currentSource,
                layerSelectedData.map((dataEntity: EntityType) => this.getEntityId(dataEntity)),
                this.entityType(),
            );
        }
    }
    // This method is used when you want to select or unselect all entities, and you have a filter active,

    // and you already select all entities without a filter
    private async handleFullSelectionFullSelectedLayerAndFilterActive(searchedEntities: EntityType[]): Promise<void> {
        const searchedEntitiesAlreadyUnselected = this.activeLayers()
            .flatMap((activeLayer) => this.unselectedEntities()[activeLayer.id])
            .filter((selectedDataEntity) =>
                searchedEntities
                    .map((searchedDataEntity) => this.getEntityId(searchedDataEntity))
                    .includes(this.getEntityId(selectedDataEntity)),
            );

        const selectAll = searchedEntitiesAlreadyUnselected.length;

        for (const layer of this.activeLayers()) {
            const currentUnselectedEntities = this.unselectedEntities()[layer.id];

            const layerSelectedData = this.handleEntitySelectionWithFilter(
                currentUnselectedEntities,
                searchedEntities,
                !selectAll,
                layer.id,
            );

            this.setSignalValue<EntityType[]>(this.unselectedEntities, layer.id, layerSelectedData);

            const currentSource = this.currentSources().find((source) => source.sourceId === layer.id);

            if (!currentSource) {
                this.mapHighlightEntityService.resetHighlight();

                continue;
            }

            await this.mapHighlightEntityService.highlightSource(
                currentSource,
                layerSelectedData.map((dataEntity) => this.getEntityId(dataEntity)),
                this.entityType(),
            );
        }
    }
    private async handleSelectAll(): Promise<void> {
        if (this.isActiveLayerFullySelected()) {
            this.unSelectAll();

            for (const source of this.currentSources()) {
                this.mapHighlightEntityService.resetHighlight(source, this.entityType());
            }
        } else {
            this.selectAll();

            for (const source of this.currentSources()) {
                await this.mapHighlightEntityService.highlightSource(
                    source,
                    [],
                    this.entityType(),
                    this.fieldInterventionBloc.value()?.fieldInterventionArea as GeoJSON.MultiPolygon | undefined,
                );
            }
        }

        for (const layer of this.activeLayers()) {
            this.setSignalValue<EntityType[]>(this.selectedEntities, layer.id, []);

            this.setSignalValue<EntityType[]>(this.unselectedEntities, layer.id, []);
        }
    }

    private hasEntity(entities: EntityType[] | undefined, entityId: string): boolean {
        return (entities ?? []).some((entity: EntityType) => this.getEntityId(entity) === entityId);
    }

    private initFullSelectedHighLight(): void {
        const excludedEntitiesIds = this.activeLayers().flatMap((activeLayer) =>
            this.unselectedEntities()[activeLayer.id].map(this.getEntityId),
        );

        for (const source of this.currentSources()) {
            this.mapHighlightEntityService.highlightSource(source, excludedEntitiesIds, this.entityType());
        }
    }

    private initHighLight(): void {
        const selectedEntities = this.activeLayers().flatMap((activeLayer) =>
            this.selectedEntities()[activeLayer.id].map(this.getEntityId),
        );

        if (!selectedEntities.length) {
            return;
        }

        for (const source of this.currentSources()) {
            this.mapHighlightEntityService.manageHighlightFeature(source, selectedEntities, this.entityType());
        }
    }

    private refreshHighlightForLayer(layerId: string): void {
        const currentSource: MapBaseSource<unknown> | undefined = this.currentSources().find(
            (source: MapBaseSource<unknown>) => source.sourceId === layerId,
        );

        if (!currentSource) {
            return;
        }

        if (this.fullSelectedLayers().includes(layerId)) {
            const excludedIds: string[] = (this.unselectedEntities()[layerId] ?? []).map((entity: EntityType) =>
                this.getEntityId(entity),
            );

            this.mapHighlightEntityService.highlightSource(currentSource, excludedIds, this.entityType());

            return;
        }

        const finalSelected: EntityType[] = this.selectedEntities()[layerId] ?? [];

        if (!finalSelected.length) {
            this.mapHighlightEntityService.resetHighlight(currentSource, this.entityType());

            return;
        }

        const selectedIds: string[] = finalSelected.map((entity: EntityType) => this.getEntityId(entity));

        this.mapHighlightEntityService.manageHighlightFeature(currentSource, selectedIds, this.entityType());
    }

    private selectAll(): void {
        this.fullSelectedLayers.set([
            ...this.fullSelectedLayers(),
            ...this.activeLayers().map((activeLayer) => activeLayer.id),
        ]);
    }

    private selectAndHighlightEntity(entity: EntityType, currentSource: MapBaseSource<unknown>): void {
        const layerId = this.getLayerId(entity);
        const entitiesSelected = this.getEntitiesToAdd(this.selectedEntities()[layerId] ?? [], entity);

        this.setSignalValue<EntityType[]>(this.selectedEntities, layerId, entitiesSelected);

        if (!entitiesSelected.length) {
            this.mapHighlightEntityService.resetHighlight(currentSource, this.entityType());

            return;
        }

        this.mapHighlightEntityService.manageHighlightFeature(
            currentSource,
            entitiesSelected.map((entity) => this.getEntityId(entity)),
            this.entityType(),
        );
    }

    private toggleEntityInLayerState(entity: EntityType): void {
        const layerId: string = this.getLayerId(entity);

        if (this.fullSelectedLayers().includes(layerId)) {
            const nextUnselected: EntityType[] = this.getEntitiesToAdd(
                this.unselectedEntities()[layerId] ?? [],
                entity,
            );

            this.setSignalValue<EntityType[]>(this.unselectedEntities, layerId, nextUnselected);

            return;
        }

        const nextSelected: EntityType[] = this.getEntitiesToAdd(this.selectedEntities()[layerId] ?? [], entity);

        this.setSignalValue<EntityType[]>(this.selectedEntities, layerId, nextSelected);
    }

    private unSelectAll(): void {
        this.fullSelectedLayers.set(
            this.fullSelectedLayers().filter(
                (layerId: string) =>
                    !this.activeLayers()
                        .map((activeLayer) => activeLayer.id)
                        .includes(layerId),
            ),
        );
    }

    private unSelectAndHighlightEntity(entity: EntityType, currentSource: MapBaseSource<unknown>): void {
        const layerId = this.getLayerId(entity);

        const fieldInterventionEntitiesUnselected = this.getEntitiesToAdd(
            this.unselectedEntities()[layerId] ?? [],
            entity,
        );

        this.setSignalValue<EntityType[]>(this.unselectedEntities, layerId, fieldInterventionEntitiesUnselected);

        this.mapHighlightEntityService.highlightSource(
            currentSource,
            fieldInterventionEntitiesUnselected.map((fieldInterventionEntity: EntityType) =>
                this.getEntityId(fieldInterventionEntity),
            ),
            this.entityType(),
        );
    }

    protected abstract buildEntityFromMapFeature(feature: MapGeoJSONFeatureWithLayer): EntityType | undefined;

    protected abstract fetchAllEntities(): Promise<EntityType[]>;

    protected abstract fetchEntities(): Promise<void>;

    protected abstract getEntityId(entity: EntityType): string;

    protected abstract getLayerId(entity: EntityType): string;
}
